import { useState, useEffect } from "react"
import {
  Building2,
  MapPin,
  Users,
  ExternalLink,
  Linkedin,
  Twitter,
  Facebook,
  Instagram,
  Send,
  Loader,
  ChevronDown,
  ChevronUp,
} from "lucide-react"
import { useNavigate, useParams } from "react-router-dom"
import { useAuth } from "../contexts/AuthProvider"
import toast from "react-hot-toast"
import Header from "../components/Header"
import ApplicationFormModal from "../components/ApplicationFormModal"
import { useMixpanel } from "../hooks/useMixpanel"
import { sendEmail } from "../services/emailService"

const EnhancedRightSidebar = ({ companyDetails, jobDetails }) => {
  const handleSocialClick = (url) => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer")
    }
  }

  return (
    <div className="lg:sticky lg:top-0 p-4 sm:p-6 lg:p-8">
      <div className="space-y-6 lg:space-y-8">
        {/* About Company */}
        <div className="space-y-3 lg:space-y-4">
          <h3 className="text-base lg:text-lg font-semibold text-slate-900">
            About {companyDetails?.name || "Company"}
          </h3>
          {companyDetails?.description && (
            <p className="text-sm text-slate-600 leading-relaxed">
              {companyDetails.description}
            </p>
          )}
        </div>

        {/* Social Links */}
        <div className="pt-4 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">
            Connect with us
          </h3>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* Website */}
            <button
              onClick={() => handleSocialClick(companyDetails?.website)}
              disabled={!companyDetails?.website}
              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-slate-700 disabled:opacity-40"
              title="Website"
            >
              <ExternalLink className="w-5 h-5" />
            </button>

            {/* LinkedIn */}
            <button
              onClick={() => handleSocialClick(companyDetails?.linkedIn)}
              disabled={!companyDetails?.linkedIn}
              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-slate-700 disabled:opacity-40"
              title="LinkedIn"
            >
              <Linkedin className="w-5 h-5" />
            </button>

            {/* Twitter */}
            <button
              onClick={() => handleSocialClick(companyDetails?.twitter)}
              disabled={!companyDetails?.twitter}
              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-slate-700 disabled:opacity-40"
              title="Twitter"
            >
              <Twitter className="w-5 h-5" />
            </button>

            {/* Facebook */}
            <button
              onClick={() => handleSocialClick(companyDetails?.facebook)}
              disabled={!companyDetails?.facebook}
              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-slate-700 disabled:opacity-40"
              title="Facebook"
            >
              <Facebook className="w-5 h-5" />
            </button>

            {/* Instagram */}
            <button
              onClick={() => handleSocialClick(companyDetails?.instagram)}
              disabled={!companyDetails?.instagram}
              className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors text-slate-700 disabled:opacity-40"
              title="Instagram"
            >
              <Instagram className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Required Skills */}
        {jobDetails.required_skills &&
          jobDetails.required_skills.length > 0 && (
            <div className="pt-4 border-t border-slate-200">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">
                Required Skills
              </h3>
              <div className="flex flex-wrap gap-2">
                {jobDetails.required_skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-lg text-xs font-medium"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
      </div>
    </div>
  )
}

const JobDetailsPage = () => {
  const navigate = useNavigate()
  const { jobId } = useParams()
  const { user } = useAuth()

  const [jobDetails, setJobDetails] = useState(null)
  const [companyDetails, setCompanyDetails] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [applyingJobId, setApplyingJobId] = useState(null)
  const [showCompanyInfo, setShowCompanyInfo] = useState(false)
  const { track } = useMixpanel()

  const [showApplicationForm, setShowApplicationForm] = useState(false)
  const [pendingApplyMeta, setPendingApplyMeta] = useState(null) // { jobId, jobTitle, orgId }

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setIsLoading(true)

        const response = await fetch(
          `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${jobId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()

        if (data.success && data.data) {
          const jobInfo = data.data.jobInfo
          const companyInfo = data.data.companyInfo

          setJobDetails(jobInfo)
          setCompanyDetails(companyInfo)

          console.log(jobInfo)
          console.log(companyInfo)
        } else {
          throw new Error(data.message || "Failed to fetch job details")
        }
      } catch (error) {
        console.error("Error fetching job details:", error)
        setError(error.message)
      } finally {
        setIsLoading(false)
      }
    }

    if (jobId) {
      fetchJobDetails()
    }
  }, [jobId])

  const sendConfirmationEmail = async () => {
    const emailContent = `
        Hi ${user?.name}, 

        Thanks for applying for the ${jobDetails?.role} role at ${jobDetails.organizationName}. We are currently reviewing your profile. We will reach out to you once we have any updates on it.

        Best,
        Patrick
        Co-founder, Resolut
        `
    const userEmail = user?.email
    sendEmail({
      email: userEmail,
      subject: `Your Application at ${jobDetails.organizationName} - Under Review`,
      text: emailContent,
    })
  }

  const handleApply = (jobId, jobTitle, orgId) => {
    if (!user || !user.authenticated) {
      toast.error("Please login to apply for jobs", {
        icon: "🔐",
        duration: 3000,
      })
      navigate("/login")
      return
    }

    if (user.accountType !== "candidate") {
      toast.error("Invalid Account Type", { icon: "📝", duration: 3000 })
      return
    }

    if (
      !user.personal_information?.currentJobTitle ||
      !user.personal_information?.yearsExperience ||
      user.personal_information?.yearsExperience === "" ||
      !user.personal_information?.currentCompany
    ) {
      toast.error("Please complete your Personal Information in your profile", {
        icon: "📝",
        duration: 4000,
      })
      setTimeout(() => navigate("/profile"), 2000)
      return
    }

    if (user.personal_information?.yearsExperience !== "0") {
      if (
        !user.job_preferences?.currentSalary ||
        user.job_preferences?.currentSalary === "" ||
        !user.job_preferences?.noticePeriod ||
        user.job_preferences?.noticePeriod === ""
      ) {
        toast.error(
          "Please complete your Job Preferences (Current Salary and Notice Period)",
          { icon: "📝", duration: 4000 },
        )
        setTimeout(() => navigate("/profile"), 2000)
        return
      }
    }

    if (!user.education || user.education.length === 0) {
      toast.error("Please add your Education details in your profile", {
        icon: "🎓",
        duration: 4000,
      })
      setTimeout(() => navigate("/profile"), 2000)
      return
    }

    if (
      user.personal_information?.yearsExperience !== "0" &&
      (!user.work_history || user.work_history.length === 0)
    ) {
      toast.error("Please add your Work History in your profile", {
        icon: "💼",
        duration: 4000,
      })
      setTimeout(() => navigate("/profile"), 2000)
      return
    }

    if (!user.resume) {
      toast.error("Please upload your Resume in your profile", {
        icon: "📄",
        duration: 4000,
      })
      setTimeout(() => navigate("/profile"), 2000)
      return
    }

    // Profile is complete — check for application form questions
    const questions = jobDetails?.applicationForm?.questions
    if (questions && questions.length > 0) {
      setPendingApplyMeta({ jobId, jobTitle, orgId })
      setShowApplicationForm(true)
      return
    }

    // No form questions — apply directly
    submitApplication(jobId, jobTitle, {})
  }

  const submitApplication = async (jobId, jobTitle, formAnswers = {}) => {
    const loadingToast = toast.loading(`Applying for ${jobTitle}...`)

    try {
      setApplyingJobId(jobId)

      const requestBody = {
        userId: user.uid,
        ...(Object.keys(formAnswers).length > 0 && {
          screeningQuestions: Object.entries(formAnswers).map(
            ([id, answer]) => ({
              question:
                jobDetails.applicationForm.questions.find((q) => q.id === id)
                  ?.text || id,
              answer,
            }),
          ),
        }),
      }

      const response = await fetch(
        `https://hire100x-e1dd4.uc.r.appspot.com/jobListings/${jobId}/apply`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      )

      const result = await response.json()

      if (response.ok && result.success) {
        sendConfirmationEmail(jobTitle)
        track("Job Applied", { user: user?.uid, job_id: jobId })
        toast.dismiss(loadingToast)
        toast.success(`Successfully applied for ${jobTitle}!`, {
          duration: 4000,
        })
      } else {
        throw new Error(
          result.message ||
            result.error ||
            `HTTP error! status: ${response.status}`,
        )
      }
    } catch (error) {
      console.error("Job application error:", error)
      toast.dismiss(loadingToast)
      toast.error(
        error.message || "Failed to submit application. Please try again.",
        { duration: 4000 },
      )
    } finally {
      setApplyingJobId(null)
      setShowApplicationForm(false)
      setPendingApplyMeta(null)
    }
  }

  const sanitizeHTML = (html) => {
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = html

    const scripts = tempDiv.getElementsByTagName("script")
    for (let i = scripts.length - 1; i >= 0; i--) {
      scripts[i].parentNode.removeChild(scripts[i])
    }

    const allElements = tempDiv.getElementsByTagName("*")
    for (let element of allElements) {
      element.removeAttribute("onclick")
      element.removeAttribute("onload")
      element.removeAttribute("onerror")
    }

    return tempDiv.innerHTML
  }

  // Remove these imports at the top:
  // import Lottie from "lottie-react"
  // import loaderAnimation from "../../src/assets/lottie/Loader.json"

  // Replace the loading state section (around line 296-307) with:

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

        <Header hideFreeTrial={true} />

        <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-64px)] overflow-hidden max-w-8xl lg:mx-[9%]">
          {/* Left Side Shimmer */}
          <div className="w-full lg:w-3/5 px-4 sm:px-6 pt-4 pb-24 lg:pb-4 overflow-y-auto hide-scrollbar bg-slate-50">
            <div className="max-w-3xl space-y-6 sm:space-y-8">
              {/* Job Header Shimmer */}
              <div className="space-y-3 pb-6 sm:pb-8 border-b border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg animate-pulse"></div>
                  <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="h-8 sm:h-10 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-28 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                  <div className="hidden sm:block h-12 w-32 bg-gray-200 rounded-3xl animate-pulse"></div>
                </div>
              </div>

              {/* Job Description Shimmer */}
              <div className="space-y-4">
                <div className="h-6 w-48 bg-gray-200 rounded animate-pulse"></div>
                <div className="space-y-3">
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-5/6 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-4/5 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>

              {/* Additional Sections Shimmer */}
              <div className="space-y-4">
                <div className="h-6 w-40 bg-gray-200 rounded animate-pulse"></div>
                <div className="space-y-3">
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-2/3 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>

              {/* Mobile Company Info Shimmer */}
              <div className="lg:hidden border-t border-slate-200 pt-6">
                <div className="h-6 w-48 bg-gray-200 rounded animate-pulse mb-4"></div>
              </div>
            </div>
          </div>

          {/* Right Side Shimmer - Desktop Only */}
          <div className="hidden lg:block bg-white w-2/5 overflow-y-auto hide-scrollbar border-l border-slate-100">
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="space-y-6 lg:space-y-8">
                {/* About Company Shimmer */}
                <div className="space-y-3 lg:space-y-4">
                  <div className="h-6 w-40 bg-gray-200 rounded animate-pulse"></div>
                  <div className="space-y-2">
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>

                {/* Social Links Shimmer */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-3"></div>
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                    {[1, 2, 3, 4, 5].map((item) => (
                      <div
                        key={item}
                        className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"
                      ></div>
                    ))}
                  </div>
                </div>

                {/* Required Skills Shimmer */}
                <div className="pt-4 border-t border-slate-200">
                  <div className="h-5 w-32 bg-gray-200 rounded animate-pulse mb-3"></div>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                      <div
                        key={item}
                        className="h-8 w-20 bg-gray-200 rounded-lg animate-pulse"
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Bottom Apply Button Shimmer - Mobile Only */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg z-50">
          <div className="w-full h-14 bg-gray-200 rounded-xl animate-pulse"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Unable to load job details
          </h2>
          <p className="text-slate-600 mb-4 px-4">{error}</p>
          <button
            onClick={() => navigate("/job-listings")}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    )
  }

  if (!jobDetails || !companyDetails) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">
            Job not found
          </h2>
          <button
            onClick={() => navigate("/job-listings")}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
        
        /* Hide scrollbar for Chrome, Safari and Opera */
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        /* Hide scrollbar for IE, Edge and Firefox */
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .job-details-container {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        .job-description-wrapper p {
          margin-bottom: 0.875rem;
          line-height: 1.7;
        }
        
        .job-description-wrapper ul,
        .job-description-wrapper ol {
          margin-left: 1.25rem;
          margin-bottom: 0.875rem;
        }
        
        .job-description-wrapper li {
          margin-bottom: 0.5rem;
          line-height: 1.7;
        }
      `}</style>

      {/* Header */}
      <Header hideFreeTrial={true} />

      {/* Split Layout - Responsive */}
      <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-64px)] overflow-hidden max-w-8xl lg:mx-[9%]">
        {/* Left Side - Job Description */}
        <div className="job-details-container w-full lg:w-3/5 px-4 sm:px-6 pt-4 pb-24 lg:pb-4 overflow-y-auto hide-scrollbar bg-slate-50">
          <div className="max-w-3xl space-y-6 sm:space-y-8">
            {/* Job Header */}
            <div className="space-y-3 pb-6 sm:pb-8 border-b border-slate-200">
              <div className="flex items-center gap-3 mb-2">
                {/* Company Logo */}
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center overflow-hidden bg-white border border-slate-200">
                  {jobDetails.organizationLogoUrl ? (
                    <img
                      src={jobDetails.organizationLogoUrl}
                      alt={`${jobDetails.organizationName || "Company"} logo`}
                      className="w-full h-full object-contain p-1.5"
                      onError={(e) => {
                        e.target.style.display = "none"
                        e.target.nextSibling.style.display = "flex"
                      }}
                    />
                  ) : null}
                  <div
                    className={`w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center ${
                      jobDetails.organizationLogoUrl ? "hidden" : "flex"
                    }`}
                  >
                    <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <span className="text-xs sm:text-sm font-medium text-slate-500 uppercase tracking-wider">
                  {jobDetails.organizationName}
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight">
                {jobDetails.role}
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
                <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm text-slate-600 flex-wrap">
                  <span className="flex items-center gap-1.5">
                    <MapPin size={14} className="sm:w-4 sm:h-4" />
                    {jobDetails.location || jobDetails.workMode || "Remote"}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Users size={14} className="sm:w-4 sm:h-4" />
                    {jobDetails.applicantCount || 0} applicants
                  </span>
                </div>

                {/* Apply Button - Desktop only */}
                <button
                  onClick={() =>
                    handleApply(
                      jobDetails.jobListingId,
                      jobDetails.role,
                      jobDetails.organizationId,
                    )
                  }
                  disabled={applyingJobId === jobDetails.jobListingId}
                  className={`hidden sm:flex px-6 py-3 rounded-3xl text-sm font-medium transition-all duration-200 items-center justify-center gap-2 ${
                    applyingJobId === jobDetails.jobListingId
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  }`}
                >
                  {applyingJobId === jobDetails.jobListingId ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Applying...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Apply Now</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Job Description */}
            {jobDetails.requirements && (
              <div className="space-y-4">
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
                  About the Role
                </h2>
                <div
                  className="job-description-wrapper text-slate-700 text-sm sm:text-[15px] leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(jobDetails.requirements),
                  }}
                />
              </div>
            )}

            {/* Mobile Company Info - Collapsible */}
            <div className="lg:hidden border-t border-slate-200 pt-6">
              <button
                onClick={() => setShowCompanyInfo(!showCompanyInfo)}
                className="w-full flex items-center justify-between text-left mb-4"
              >
                <h2 className="text-lg font-semibold text-slate-900">
                  Company Information
                </h2>
                {showCompanyInfo ? (
                  <ChevronUp className="w-5 h-5 text-slate-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-600" />
                )}
              </button>

              {showCompanyInfo && (
                <div className="bg-white rounded-lg border border-slate-200">
                  <EnhancedRightSidebar
                    companyDetails={companyDetails}
                    jobDetails={jobDetails}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Side - Desktop Only - Company Info */}
        <div className="hidden lg:block job-details-container bg-white w-2/5 overflow-y-auto hide-scrollbar border-l border-slate-100">
          <EnhancedRightSidebar
            companyDetails={companyDetails}
            jobDetails={jobDetails}
          />
        </div>
      </div>

      {/* Fixed Bottom Apply Button - Mobile Only */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg z-50">
        <button
          onClick={() =>
            handleApply(
              jobDetails.jobListingId,
              jobDetails.role,
              jobDetails.organizationId,
            )
          }
          disabled={applyingJobId === jobDetails.jobListingId}
          className={`w-full py-4 rounded-xl text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
            applyingJobId === jobDetails.jobListingId
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-slate-900 hover:bg-slate-800 text-white"
          }`}
        >
          {applyingJobId === jobDetails.jobListingId ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Applying...</span>
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>Apply Now</span>
            </>
          )}
        </button>
      </div>

      {showApplicationForm && pendingApplyMeta && (
        <ApplicationFormModal
          questions={jobDetails.applicationForm.questions}
          jobTitle={pendingApplyMeta.jobTitle}
          isSubmitting={!!applyingJobId}
          onSubmit={(answers) =>
            submitApplication(
              pendingApplyMeta.jobId,
              pendingApplyMeta.jobTitle,
              answers,
            )
          }
          onClose={() => {
            setShowApplicationForm(false)
            setPendingApplyMeta(null)
          }}
        />
      )}
    </div>
  )
}

export default JobDetailsPage
